import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  Typography,
  createStyles,
  withStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

export interface FaqItem {
  title: string;
  options: string[];
}

export interface FaqSet {
  title: string;
  items: FaqItem[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  description: string;
  sets: FaqSet[];
  classes: Record<string, string>;
}

const styles = (theme: any) =>
  createStyles({
    paper: {
      width: 500,
      maxWidth: "100%",
      padding: 24,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: 12,
      backgroundColor: magicLinkColors.surface,
    },
    top: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },
    title: {
      fontFamily: "Nunito Sans",
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: "33px",
      color: magicLinkColors.textStrong,
    },
    line: {
      height: 1,
      backgroundColor: theme.palette.primary.main,
    },
    description: {
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "20px",
      color: magicLinkColors.text,
    },
    set: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      padding: "8px 0",
    },
    setTitle: {
      fontFamily: "Nunito Sans",
      fontSize: "19px",
      fontWeight: 600,
      lineHeight: "26px",
      color: magicLinkColors.textStrong,
    },
    item: {
      border: `1px solid ${magicLinkColors.border}`,
      borderRadius: `${magicLinkRadius.card}px !important`,
      boxShadow: "none",
      margin: "0 !important",
      "&::before": {
        display: "none",
      },
    },
    itemExpanded: {
      borderColor: theme.palette.primary.main,
    },
    itemSummary: {
      minHeight: "52px !important",
      padding: "0 12px",
    },
    itemSummaryContent: {
      margin: "16px 0 !important",
    },
    itemTitle: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "20px",
      color: magicLinkColors.text,
    },
    itemTitleExpanded: {
      fontSize: "14px",
      color: magicLinkColors.textStrong,
    },
    itemIcon: {
      fontSize: 16,
      color: magicLinkColors.textStrong,
    },
    itemDetails: {
      flexDirection: "column",
      gap: 8,
      padding: "0 12px 12px",
      borderTop: `1px solid ${magicLinkColors.border}`,
    },
    answer: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "20px",
      color: magicLinkColors.text,
      paddingTop: 12,
    },
  });

const FaqPanel = ({ open, onClose, description, sets, classes }: Props) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    classes={{ paper: classes.paper }}
  >
    <Box className={classes.top}>
      <Typography component="h2" className={classes.title}>
        Got questions?
      </Typography>
      <IconButton aria-label="Close" size="small" onClick={onClose} id="closeFaq">
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box className={classes.line} />
    <Typography className={classes.description}>{description}</Typography>
    {sets.map((set) => (
      <Box key={set.title} className={classes.set}>
        <Typography className={classes.setTitle}>{set.title}</Typography>
        {set.items.map((item) => (
          <Accordion
            key={item.title}
            classes={{ root: classes.item, expanded: classes.itemExpanded }}
            elevation={0}
          >
            <AccordionSummary
              classes={{
                root: classes.itemSummary,
                content: classes.itemSummaryContent,
              }}
              expandIcon={<ExpandMoreIcon className={classes.itemIcon} />}
            >
              <Typography className={classes.itemTitle}>
                {item.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.itemDetails}>
              {item.options.map((option) => (
                <Typography key={option} className={classes.answer}>
                  {option}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    ))}
  </Drawer>
);

export default withStyles(styles)(FaqPanel);
